/**
 * useWishlist Hook
 * Manages "My Picks" wishlist state and operations
 * 
 * Responsibilities:
 * - Wishlist items management (add, remove, toggle)
 * - Local persistence (localStorage for guests)
 * - Firebase persistence (Firestore for logged-in users)
 * - Toast notifications for wishlist actions
 * - Source tracking (editor-picks, web-search, ai-chat)
 * 
 * Storage Pattern:
 * - Guest: localStorage only (heseda-ai_wishlist)
 * - Logged in: localStorage + Firebase sync (masterProfiles/{uid}/wishlist)
 * 
 * @author Heseda AI Team
 * @version 1.0
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { doc, updateDoc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../services/firebaseConfig';
import type { WishlistItem } from '../types';

interface UseWishlistProps {
  language?: string;
  userId?: string | null;
  onToast?: (message: string) => void;
}

interface UseWishlistReturn {
  // Wishlist state
  wishlistItems: WishlistItem[];
  wishlistCount: number;
  
  // Wishlist operations
  addToWishlist: (product: WishlistItem, source?: WishlistSource) => void;
  removeFromWishlist: (productId: string) => void;
  toggleWishlist: (product: WishlistItem, source?: WishlistSource) => void;
  isInWishlist: (productId: string) => boolean;
  clearWishlist: () => void;
  
  // Sync status
  isSyncing: boolean;
}

/** Source tracking for analytics - where the item was added from */
export type WishlistSource = 'editor-picks' | 'web-search' | 'ai-chat' | 'product-page';

interface WishlistItemWithMeta extends WishlistItem {
  /** Where the item was added from (for analytics) */
  addedFrom?: WishlistSource;
}

const WISHLIST_STORAGE_KEY = 'heseda-ai_wishlist';

// Migrate legacy localStorage key
try {
  const old = localStorage.getItem('skinmatch_wishlist');
  if (old && !localStorage.getItem(WISHLIST_STORAGE_KEY)) {
    localStorage.setItem(WISHLIST_STORAGE_KEY, old);
    localStorage.removeItem('skinmatch_wishlist');
  }
} catch { /* ignore */ }

/**
 * Custom hook for managing wishlist/saved products functionality
 */
export function useWishlist({ 
  language = 'ko',
  userId,
  onToast,
}: UseWishlistProps = {}): UseWishlistReturn {
  
  // Wishlist items state with localStorage persistence
  const [wishlistItems, setWishlistItems] = useState<WishlistItemWithMeta[]>(() => {
    try {
      const savedWishlist = localStorage.getItem(WISHLIST_STORAGE_KEY);
      return savedWishlist ? JSON.parse(savedWishlist) : [];
    } catch (error) {
      console.warn('[useWishlist] Failed to load from localStorage:', error);
      return [];
    }
  });
  
  const [isSyncing, setIsSyncing] = useState(false);
  const hasLoadedFromFirebase = useRef(false);
  const previousUserId = useRef<string | null | undefined>(undefined);
  
  // Track current userId for saveToFirebase closure
  const currentUserIdRef = useRef<string | null | undefined>(userId);
  currentUserIdRef.current = userId;

  // Ref for latest wishlist items (avoids stale closure in callbacks)
  const wishlistItemsRef = useRef(wishlistItems);
  wishlistItemsRef.current = wishlistItems;

  // Serialization for Firebase saves: prevents race conditions
  const saveVersionRef = useRef(0);
  const pendingSaveRef = useRef<Promise<void>>(Promise.resolve());

  // Calculate wishlist count
  const wishlistCount = wishlistItems.length;

  /**
   * Show toast notification
   */
  const showToast = useCallback((message: string) => {
    if (onToast) {
      onToast(message);
    }
  }, [onToast]);

  /**
   * Persist wishlist to localStorage
   */
  useEffect(() => {
    try {
      localStorage.setItem(WISHLIST_STORAGE_KEY, JSON.stringify(wishlistItems));
    } catch (error) {
      console.warn('[useWishlist] Failed to save to localStorage:', error);
    }
  }, [wishlistItems]);

  /**
   * Reset refs and clear wishlist when user logs out
   */
  useEffect(() => {
    if (!userId && previousUserId.current) {
      // User just logged out - reset sync tracking
      console.log('[useWishlist] User logged out, resetting sync state');
      hasLoadedFromFirebase.current = false;
      previousUserId.current = undefined;
      // Clear wishlist on logout to prevent data leaking
      setWishlistItems([]);
      try {
        localStorage.removeItem(WISHLIST_STORAGE_KEY);
      } catch (e) {
        console.warn('[useWishlist] Failed to clear localStorage on logout:', e);
      }
    }
  }, [userId]);

  /**
   * Sync wishlist with Firebase when user logs in
   * Uses Firebase as source of truth, merges with any local items
   */
  useEffect(() => {
    // No user - nothing to sync
    if (!userId) {
      return;
    }
    
    // Skip if we already loaded for this user
    if (previousUserId.current === userId && hasLoadedFromFirebase.current) {
      return;
    }
    
    const syncWithFirebase = async () => {
      setIsSyncing(true);
      console.log('[useWishlist] Starting Firebase sync for user:', userId);
      
      try {
        const profileRef = doc(db, 'masterProfiles', userId);
        const profileSnap = await getDoc(profileRef);
        
        if (profileSnap.exists()) {
          const data = profileSnap.data();
          const firebaseWishlist = (data.wishlist || []) as WishlistItemWithMeta[];
          
          // Capture local wishlist state at sync start (closure captures this at effect creation time)
          // This represents items added as guest before login
          const localWishlistAtSyncStart = wishlistItems;
          
          // Merge: Firebase items + local items not already in Firebase
          const firebaseIds = new Set(firebaseWishlist.map(item => item.id));
          const newLocalItems = localWishlistAtSyncStart.filter(item => !firebaseIds.has(item.id));
          
          let finalWishlist: WishlistItemWithMeta[];
          
          if (newLocalItems.length > 0) {
            // Have new local items - merge and save back
            finalWishlist = [...firebaseWishlist, ...newLocalItems];
            await updateDoc(profileRef, { wishlist: finalWishlist });
            console.log('[useWishlist] Merged local items with Firebase wishlist');
          } else {
            // No new local items - use Firebase wishlist as source of truth
            finalWishlist = firebaseWishlist;
          }
          
          setWishlistItems(finalWishlist);
          console.log('[useWishlist] Loaded wishlist from Firebase:', finalWishlist.length, 'items');
        } else if (wishlistItems.length > 0) {
          // No Firebase profile yet, save local wishlist
          await setDoc(profileRef, { wishlist: wishlistItems }, { merge: true });
          console.log('[useWishlist] Saved local wishlist to new Firebase profile');
        } else {
          // No Firebase profile and no local items - ensure wishlist is empty
          setWishlistItems([]);
        }
        
        hasLoadedFromFirebase.current = true;
        previousUserId.current = userId;
      } catch (error) {
        console.warn('[useWishlist] Firebase sync failed:', error);
        // On error, still mark as loaded to prevent infinite retries
        hasLoadedFromFirebase.current = true;
        previousUserId.current = userId;
      } finally {
        setIsSyncing(false);
      }
    };
    
    syncWithFirebase();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]); // Only depend on userId, not wishlistItems to avoid loops

  /**
   * Save wishlist to Firebase (serialized to prevent race conditions)
   * Uses a version counter to skip stale intermediate saves
   */
  const saveToFirebase = useCallback((items: WishlistItemWithMeta[]) => {
    const uid = currentUserIdRef.current;
    if (!uid) {
      console.log('[useWishlist] No user, skipping Firebase save');
      return;
    }
    
    const version = ++saveVersionRef.current;
    console.log('[useWishlist] Queuing Firebase save v' + version + ':', items.length, 'items');
    
    pendingSaveRef.current = pendingSaveRef.current.then(async () => {
      // Skip if a newer save was queued while we were waiting
      if (saveVersionRef.current !== version) {
        console.log('[useWishlist] Skipping stale save v' + version + ' (latest: v' + saveVersionRef.current + ')');
        return;
      }
      // Skip if user changed or logged out since save was queued
      if (currentUserIdRef.current !== uid) {
        console.log('[useWishlist] Skipping save v' + version + ': user changed');
        return;
      }
      
      try {
        const profileRef = doc(db, 'masterProfiles', uid);
        await updateDoc(profileRef, { wishlist: items });
        console.log('[useWishlist] Wishlist saved to Firebase successfully');
      } catch (error) {
        // If document doesn't exist, create it
        try {
          const profileRef = doc(db, 'masterProfiles', uid);
          await setDoc(profileRef, { wishlist: items }, { merge: true });
          console.log('[useWishlist] Wishlist saved to new Firebase profile');
        } catch (setError) {
          console.warn('[useWishlist] Failed to save to Firebase:', setError);
          // Notify user so they know the change may not persist
          showToast(language === 'en'
            ? '⚠️ Failed to sync — changes may not persist'
            : '⚠️ 동기화 실패 — 변경사항이 저장되지 않을 수 있습니다');
        }
      }
    }).catch(() => {
      // Prevent unhandled rejection from breaking the chain
    });
  }, [language, showToast]); // Include language and showToast for error notification

  /**
   * Check if a product is in the wishlist
   */
  const isInWishlist = useCallback((productId: string): boolean => {
    return wishlistItems.some(item => item.id === productId);
  }, [wishlistItems]);

  /**
   * Add a product to the wishlist
   */
  const addToWishlist = useCallback((product: WishlistItem, source?: WishlistSource) => {
    // Check if already in wishlist (use ref for latest items)
    if (wishlistItemsRef.current.some(item => item.id === product.id)) {
      showToast(language === 'en' 
        ? `${product.name} is already saved!`
        : `${product.name}은(는) 이미 저장되었습니다!`);
      return;
    }

    const itemWithMeta: WishlistItemWithMeta = {
      ...product,
      addedAt: Date.now(),
      addedFrom: source, // Analytics - where the item was added from
    };

    const newItems = [...wishlistItemsRef.current, itemWithMeta];
    // Update ref synchronously so rapid successive calls see the latest list
    wishlistItemsRef.current = newItems;
    setWishlistItems(newItems);
    saveToFirebase(newItems);
    
    showToast(language === 'en'
      ? `❤️ ${product.name} saved to My Picks!`
      : `❤️ ${product.name}이(가) 내 픽에 저장되었습니다!`);
  }, [language, saveToFirebase, showToast]);

  /**
   * Remove a product from the wishlist
   */
  const removeFromWishlist = useCallback((productId: string) => {
    const removedItem = wishlistItemsRef.current.find(item => item.id === productId);
    const newItems = wishlistItemsRef.current.filter(item => item.id !== productId);
    // Update ref synchronously so rapid successive calls see the latest list
    wishlistItemsRef.current = newItems;
    setWishlistItems(newItems);
    saveToFirebase(newItems);
    
    if (removedItem) {
      showToast(language === 'en'
        ? `💔 ${removedItem.name} removed from My Picks`
        : `💔 ${removedItem.name}이(가) 내 픽에서 제거되었습니다`);
    }
  }, [language, saveToFirebase, showToast]);

  /**
   * Toggle a product in/out of the wishlist
   */
  const toggleWishlist = useCallback((product: WishlistItem, source?: WishlistSource) => {
    if (isInWishlist(product.id)) {
      removeFromWishlist(product.id);
    } else {
      addToWishlist(product, source);
    }
  }, [addToWishlist, isInWishlist, removeFromWishlist]);

  /**
   * Clear all items from the wishlist
   */
  const clearWishlist = useCallback(() => {
    setWishlistItems([]);
    saveToFirebase([]);
    showToast(language === 'en' 
      ? '🗑️ My Picks cleared'
      : '🗑️ 내 픽이 비워졌습니다');
  }, [language, saveToFirebase, showToast]);

  return {
    // State
    wishlistItems,
    wishlistCount,
    
    // Operations
    addToWishlist,
    removeFromWishlist,
    toggleWishlist,
    isInWishlist,
    clearWishlist,
    
    // Sync status
    isSyncing,
  };
}

export default useWishlist;
